import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';
import { AppLinksService } from '../applinks/app-links.service';
import { removeSpace } from '../codeUtils';
import { ConfigService } from '../config/config.service';
import { EventsService } from '../event/event.service';
import { GameHubControllerService } from '../gameHub/game-hub-controller';
import { UserSessionService } from '../user-session/user-session.service';
import { UtilsService } from '../utils/utils.service';
import { WebcontentExecuteService } from './webcontent-execute.service';
import { CashierContentService } from '../cashier/cashier-content.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GameTileService } from '../game-tile/game-tile.service';
import { RoutingService } from '../routing/routing.service';
import { Navigator } from '../routing/navigator';
import { ControlService } from '../control/control.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})

export class WebcontentService {

  private controlService;
  public gameData: any;
  public homePage = this.config.getConfig()?.FEATURE_HOMEPAGE ?? 'terms';
  public isAuthenticated: boolean = false;

  private _externalLinkSubject = new BehaviorSubject(this.homePage);
  public _externalLinkValue = this._externalLinkSubject.asObservable();

  constructor(
    private appLinks: AppLinksService,
    private cashierContentService: CashierContentService,
    private config: ConfigService,
    private events: EventsService,
    private gameHubControllerService: GameHubControllerService,
    private gameTile: GameTileService,
    private injector: Injector,
    private navigator: Navigator,
    private routing: RoutingService,
    private userSessionService: UserSessionService,
    private utilsService: UtilsService,
    private webcontentExecuteService: WebcontentExecuteService,
  ) {
    this.setIframeEventListeners();

    this.userSessionService.sessionToken$
      .pipe(
        map(token => !!token),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.isAuthenticated = this.userSessionService.isAuthenticated();
      });
  }

  public setIframeEventListeners() {

    window.addEventListener('message', (event: MessageEvent) => {

      // only href on <a> tag
      if (event.data.type === 'navNewWindow') {
        return this.onNavNewWindow(event);
      }

      // on empty class with no http
      if (event.data.type === 'navSameWindow') {
        return this.onNavSameWindow(event);
      }

      // use class="button-webview-page" with href on <a> tag
      if (event.data.type === 'navOpenWebView') {
        return this.onNavOpenWebView(event);
      }

      // use class="button-app-page" with href on <a> tag
      if (event.data.type === 'navOpenApp') {
        return this.onNavOpenApp(event);
      }

      // use class="button-launch-game" with href on <a> tag
      if (event.data.type === 'launchGame') {
        return this.launchGame();
      }

      // use class="button-execute-method" with href on <a> tag This requires changes in webservices. 
      if (event.data.type === 'executeMethod') {
        // the basic idea is to get a method name from service and execute it our end.
        return this.webcontentExecuteService.executeMethod(event);
      }
    });

  }

  private onNavNewWindow(event: MessageEvent) {
    return this.appLinks.openExternalPage(event.data.params);
  }

  private onNavSameWindow(event: MessageEvent) {
    return this.onNavOpenWebView(event);
  }

  private onNavOpenWebView(event: MessageEvent) {
    const url = event.data.params;
    const pageName = this.utilsService.cleanPathName(url);
    this.updateExternalLink(pageName);
    this.events.publish("event:reload:externallinks");
    this.navigator.dismissAll();
    this.controlService = this.injector.get(ControlService);
    this.controlService.setCategoryForWordPressPage();
    this.routing.toWebviewTab(false, url);
  }

  private onNavOpenApp(event?: MessageEvent): any {
    const pageName: string = event.data.params.href;
    //Media and Title are interchange to read the value in the eventValue code
    //postToParent(href, title, media) from Configuration File
    const depositCode: string = event.data.params?.title;
    const signupCode: string = event.data.params?.media;
    const params: any = {};
    if (!!depositCode) params.depositcode = depositCode;
    if (!!signupCode) params.signupcode = signupCode;
    if (this.isAuthenticated) {
      return this.onNavOpenAppAuthenticated(pageName, params);
    } else {
      return this.onNavOpenAppNotAuthenticated(pageName);
    }
  }

  private onNavOpenAppAuthenticated(pageName?: string, params?: any): any {
    if (pageName.charAt(0) == '/') pageName = pageName?.substring(1);
    if (pageName?.slice(0, 5) == 'CCNP_') return this.onContentPlaceholder(pageName);

    switch (pageName) {
      case 'LoginComponent':
        const messageLogin = "You are already logged in."
        this.showLoggedinPopup(messageLogin);
        break;
      case 'SignupStepOneComponent':
        const messageStepOne = "You already have an account with us. Some offers are only available for new accounts. Please see offer terms and conditions for more information."
        this.showLoggedinPopup(messageStepOne);
        break;
      case 'SignupStepTwoComponent':
        const messageStepTwo = "You already have an account with us. Some offers are only available for new accounts. Please see offer terms and conditions for more information."
        this.showLoggedinPopup(messageStepTwo);
        break;
      case 'SignupOptionsComponent':
        const messageStepOptions = "You already have an account with us. Some offers are only available for new accounts. Please see offer terms and conditions for more information."
        this.showLoggedinPopup(messageStepOptions);
        break;
      case 'DepositMainComponent':
        if (params && params.depositcode) {
          this.cashierContentService.updateBonusOfferValue(params.depositcode);
        }
        this.navigator.navigateTo(pageName, { multipleModals: true }, params);
        break;
      default:
        this.navigator.navigateTo(pageName, { multipleModals: true }, params);
        break;
    }
  }

  private showLoggedinPopup(message?: string) {
    const popoverComp = 'PopoverAlertComponent';

    const data: any = {
      content: message,
    };

    return this.navigator.openPopover(popoverComp, data);
  }

  private onNavOpenAppNotAuthenticated(pageName?: string, params?: any): any {
    if (pageName.charAt(0) == '/') pageName = pageName?.substring(1);

    switch (pageName) {
      case 'SignupStepOneComponent':
        this.navigator.navigateTo('SignupStepOneComponent');
        break;
      case 'SignupStepTwoComponent':
        this.navigator.navigateTo('SignupStepTwoComponent');
        break;
      case 'SignupOptionsComponent':
        this.navigator.navigateTo('SignupOptionsComponent');
        break;
      default:
        this.navigator.setWaitingModal(pageName, params);
        this.navigator.navigateTo('LoginComponent');
        break;
    }
  }

  private launchGame(): any {
    this.gameData = this.gameTile.waitingGameData;
    if (!this.isAuthenticated) return this.launchGameLogout(this.gameData)
    return this.gameHubControllerService.launchCasinoGame(this.gameData);
  }

  private launchGameLogout(gameData): any {
    this.gameTile.waitingGameData = gameData;
    return this.navigator.navigateTo('LoginComponent');
  }

  public updateExternalLink(value: any) {
    return this._externalLinkSubject.next(value);

  }

  private onContentPlaceholder(pageName?: string) {
    const link: string = pageName?.replace("CCNP_", "");
    const cleanedLink = removeSpace(link);
    return this.navigator.navigateTo('ContentPlaceholderComponent', { multipleModals: true }, { link: cleanedLink });
  }

  getDocContentUrl(page: string) {
    const webviewConfigUrl = this.config.getConfig().WORDPRESS_URL;
    const webservicesUrl = this.config.getConfig().WEBSERVICES_URL;

    if (page != "/") page = "/" + page;
    if (page == "/home") page = "/";

    const url = `${webviewConfigUrl}${page}`;
    const fullUrl = this.springbootCheck(url);
    const clientapp = this.config.getClientApp();

    return `${webservicesUrl}/external/get-doc-content?page=${fullUrl}&clientapp=${clientapp}`;
  }

  springbootCheck(url: string): string {
    if (url[url.length - 1] != "/") return url = url + "/";
    return url;
  }

  public updateWebcontentLink(value: any) {
    return this._externalLinkSubject.next(value);
  }

}
